import { createContext, use } from 'react';

import type { Facet } from './library';
import type { TagChanges } from './tags';
import type { Asset, AssetKind, Collection, Owner, SmartRule } from './types';

export type Upload = {
  id: string;
  name: string;
  size: number;
  /** 0–100. */
  progress: number;
  /** Uploading → processing (finishing on the server) → complete. */
  status: 'uploading' | 'processing' | 'complete';
  kind: AssetKind;
  preview?: string;
};

export type MediaStore = {
  /** True until the library first loads. */
  loading: boolean;
  assets: Asset[];
  collections: Collection[];
  /** The brand, then each persona: who assets can belong to. */
  owners: Owner[];
  /** The library's filters (Account depends on the owners). */
  facets: Facet[];
  /** What a smart collection's rule can use: the filters, tags and competitor signals. */
  ruleFacets: Facet[];
  storage: { usedBytes: number; limitBytes: number };
  uploads: Upload[];
  addToCollection: (collectionId: string, assetIds: string[]) => void;
  removeFromCollection: (collectionId: string, assetId: string) => void;
  removeManyFromCollection: (collectionId: string, assetIds: string[]) => void;
  /** Resolves to the new collection, or undefined when saving failed (already toasted). */
  createCollection: (
    name: string,
    init: { assetIds?: string[]; rule?: SmartRule },
  ) => Promise<Collection | undefined>;
  /** Rename a collection or change a smart one's rule; resolves to it, or undefined on failure. */
  updateCollection: (
    collectionId: string,
    changes: { name?: string; rule?: SmartRule },
  ) => Promise<Collection | undefined>;
  /** Save what a collection holds today as a new hand-picked one. */
  copyCollection: (collectionId: string) => Promise<Collection | undefined>;
  /** Resolves to whether it was deleted. */
  deleteCollection: (collectionId: string) => Promise<boolean>;
  /** The workspace the library belongs to ('' until it loads). */
  workspaceId: string;
  addTag: (assetId: string, tag: string) => void;
  removeTag: (assetId: string, tag: string) => void;
  /** Adds and removes tags on many assets at once. */
  tagAssets: (assetIds: string[], changes: TagChanges) => void;
  trash: (assetIds: string[]) => void;
  /** Adds files to the library, and to a hand-picked collection if given. */
  upload: (files: File[], options?: { collectionId?: string }) => void;
  cancelUpload: (id: string) => void;
  dismissUploads: () => void;
};

export const MediaContext = createContext<MediaStore | null>(null);

export function useMedia(): MediaStore {
  const store = use(MediaContext);
  if (!store) throw new Error('useMedia must be used inside <MediaProvider>');
  return store;
}
